import { useNavigate, useSearch } from "@tanstack/react-router";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCan } from "@/lib/auth";

import type { CodeRagSearch, CodeRagTab } from "..";
import { AskTab } from "../components/AskTab";
import { HowToTab } from "../components/HowToTab";
import { ManageQdrantTab } from "../components/ManageQdrantTab";
import { OverviewTab } from "../components/OverviewTab";
import { RagCommandTab } from "../components/RagCommandTab";
import { SearchTab } from "../components/SearchTab";

export function CodeRagPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/code-rag" }) as CodeRagSearch;

  // Manage Qdrant is its own permission, outside read/write, so a Monitors
  // category grant can never reach it. A direct URL hit without it falls back
  // to overview rather than 403-ing — same idiom as posthog's settings tab.
  const canManage = useCan("code_rag.admin");
  const requested: CodeRagTab = search.tab ?? "overview";
  const tab: CodeRagTab = requested === "manage" && !canManage ? "overview" : requested;

  const setSearch = (next: Partial<CodeRagSearch>) =>
    navigate({
      to: "/code-rag",
      search: (prev: CodeRagSearch) => ({ ...prev, ...next }),
    });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Code RAG</h1>
        <p className="text-sm text-muted-foreground">
          A semantic index of every Cinch product repo — how current it is, and a
          way to search it by meaning rather than by exact symbol.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setSearch({ tab: v as CodeRagTab })}>
        <TabsList variant="underline">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="ask">Ask</TabsTrigger>
          <TabsTrigger value="rag-command">Using /rag</TabsTrigger>
          {canManage ? <TabsTrigger value="manage">Manage Qdrant</TabsTrigger> : null}
          <TabsTrigger value="how-to">How to use</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab onSearchRepo={(repo) => setSearch({ tab: "search", repo })} />
        </TabsContent>
        <TabsContent value="search" className="mt-6">
          <SearchTab search={search} setSearch={setSearch} />
        </TabsContent>
        <TabsContent value="ask" className="mt-6">
          <AskTab search={search} setSearch={setSearch} />
        </TabsContent>
        {canManage ? (
          <TabsContent value="manage" className="mt-6">
            <ManageQdrantTab />
          </TabsContent>
        ) : null}
        <TabsContent value="rag-command" className="mt-6">
          <RagCommandTab />
        </TabsContent>
        <TabsContent value="how-to" className="mt-6">
          <HowToTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
